// List Stacking — data access helpers (S2 / D50 / D53).
// All reads/writes hit raw_leads + the S2 tables (lists / raw_lead_lists /
// raw_lead_imports) + the list_stacking_lists view. NOTHING here touches leads,
// me_call_queue, create_lead_intake, the dialer, or the drip runner — new records
// are created through the import_raw_leads RPC, which itself writes via the live
// intake path so the S1 mirror shadows them into raw_leads (D56).
import { getSupabaseClient } from "../providers/supabase/supabase";

export type ListRow = {
  id: string;
  title: string;
  case_type: string | null;
  source: string | null;
  total_records: number;
  direct_mail: number;
  cold_calling: number;
  sms: number;
  rvm: number;
  leads_converted: number;
  offers_made: number;
  under_contract: number;
  deals: number;
};

export type RawLeadRow = {
  id: string;
  record_status: string;
  campaign_id: string | null;
  campaign_name: string | null;
  promoted_lead_id: string | null;
  source_lead_id: string | null;
  primary_contact_id: string | null;
  vintage_date: string | null;
  case_type: string | null;
  call_attempts: number;
  do_not_call: boolean;
  property_address: string;
  owner: string;
  county: string | null;
  ownership: string;
  record_type: "Complete" | "Partial";
  list_count: number;
  tag_count: number;
  phone_count: number;
  best_number: string | null;
  best_score: number | null;
  last_call: string | null;
  in_leads: boolean;
};

// Server-side filter model for the Property Addresses grid (D64).
export type GridFilters = {
  search?: string;
  listId?: string | null;
  campaignId?: string | null;    // "__unassigned__" => campaign_id is null
  caseType?: string | null;
  tagId?: string | null;
  doNotCall?: "exclude" | "only" | null;
  vintageFrom?: string | null;
  vintageTo?: string | null;
  neverCalled?: boolean;         // call_attempts = 0
  newThisWeek?: boolean;         // vintage_date >= today-7
  lastCall?: string | null;      // activities.disposition (via source_lead_id prefilter)
};
export type GridParams = {
  page: number;
  pageSize: number;
  sort?: { field: string; asc: boolean };
  filters: GridFilters;
};

const asArray = <T,>(v: T | T[] | null | undefined): T[] =>
  Array.isArray(v) ? v : v ? [v] : [];

// ---- Dashboard counts -------------------------------------------------------
export async function loadDashboard() {
  const sb = getSupabaseClient();
  const today = new Date().toISOString().slice(0, 10);
  const countOf = async (build: (q: any) => any) => {
    const { count } = await build(
      sb.from("raw_leads").select("*", { count: "exact", head: true }).is("duplicate_of", null),
    );
    return count ?? 0;
  };
  const [total, newToday, unassigned, promoted, caseRows, lists] = await Promise.all([
    countOf((q) => q),
    countOf((q) => q.eq("vintage_date", today)),
    countOf((q) => q.is("campaign_id", null)),
    countOf((q) => q.eq("record_status", "promoted")),
    sb.from("raw_leads").select("case_type").is("duplicate_of", null),
    sb.from("list_stacking_lists").select("title,total_records").order("total_records", { ascending: false }).limit(8),
  ]);
  const byCase: Record<string, number> = {};
  for (const r of (caseRows.data ?? []) as any[]) {
    const k = r.case_type || "(uncategorized)";
    byCase[k] = (byCase[k] ?? 0) + 1;
  }
  return {
    total, newToday, unassigned, promoted,
    byCase: Object.entries(byCase).sort((a, b) => b[1] - a[1]),
    byList: (lists.data ?? []) as { title: string; total_records: number }[],
  };
}

// ---- Property Addresses grid (server-side pagination + filters, D64) --------
const SORT_COLS: Record<string, string> = {
  vintage: "vintage_date", attempts: "call_attempts", status: "record_status", case_type: "case_type",
};
const daysAgoISO = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const sanitize = (s: string) => s.replace(/[%,()*]/g, " ").trim();

// Resolve the raw_lead id set matching a membership/tag/last-call filter (for
// intersection via chained .in('id', …)). Returns null when the filter is unset
// (no constraint) and [] when it matches nothing (short-circuit to empty result).
async function resolveIdConstraints(sb: any, f: GridFilters): Promise<{ ids: string[] | null; sourceLeadIds: string[] | null }> {
  let ids: string[] | null = null;
  const intersect = (next: string[]) => { ids = ids === null ? next : ids.filter((x) => next.includes(x)); };
  if (f.listId) {
    const { data } = await sb.from("raw_lead_lists").select("raw_lead_id").eq("list_id", f.listId);
    intersect((data ?? []).map((r: any) => r.raw_lead_id));
  }
  if (f.tagId) {
    const { data } = await sb.from("taggables").select("taggable_id").eq("taggable_type", "raw_lead").eq("tag_id", f.tagId);
    intersect((data ?? []).map((r: any) => r.taggable_id));
  }
  let sourceLeadIds: string[] | null = null;
  if (f.lastCall) {
    const { data } = await sb.from("activities").select("lead_id").eq("type", "call").eq("disposition", f.lastCall);
    sourceLeadIds = [...new Set((data ?? []).map((r: any) => r.lead_id).filter(Boolean))] as string[];
  }
  return { ids, sourceLeadIds };
}

export async function loadRawLeads(params: GridParams): Promise<{ rows: RawLeadRow[]; total: number }> {
  const sb = getSupabaseClient();
  const { page, pageSize, sort, filters: f } = params;
  const search = f.search ? sanitize(f.search) : "";

  const { ids, sourceLeadIds } = await resolveIdConstraints(sb, f);
  if (ids !== null && ids.length === 0) return { rows: [], total: 0 };
  if (sourceLeadIds !== null && sourceLeadIds.length === 0) return { rows: [], total: 0 };

  const propSel = search
    ? "properties!inner(address_line1,city,county,owner_1_name,ownership_flags,record_completeness)"
    : "properties(address_line1,city,county,owner_1_name,ownership_flags,record_completeness)";
  let q = sb
    .from("raw_leads")
    .select(
      `id,record_status,campaign_id,promoted_lead_id,source_lead_id,primary_contact_id,vintage_date,case_type,call_attempts,do_not_call,` +
        `${propSel},campaigns(name),raw_lead_lists(list_id)`,
      { count: "exact" },
    )
    .is("duplicate_of", null);

  if (f.campaignId === "__unassigned__") q = q.is("campaign_id", null);
  else if (f.campaignId) q = q.eq("campaign_id", f.campaignId);
  if (f.caseType) q = q.eq("case_type", f.caseType);
  if (f.doNotCall === "exclude") q = q.eq("do_not_call", false);
  if (f.doNotCall === "only") q = q.eq("do_not_call", true);
  if (f.neverCalled) q = q.eq("call_attempts", 0);
  if (f.newThisWeek) q = q.gte("vintage_date", daysAgoISO(7));
  if (f.vintageFrom) q = q.gte("vintage_date", f.vintageFrom);
  if (f.vintageTo) q = q.lte("vintage_date", f.vintageTo);
  if (ids !== null) q = q.in("id", ids);
  if (sourceLeadIds !== null) q = q.in("source_lead_id", sourceLeadIds);
  if (search) q = q.or(`address_line1.ilike.%${search}%,owner_1_name.ilike.%${search}%`, { referencedTable: "properties" });

  const sortCol = (sort && SORT_COLS[sort.field]) || "vintage_date";
  q = q.order(sortCol, { ascending: sort ? sort.asc : false, nullsFirst: false });
  const from = (page - 1) * pageSize;
  q = q.range(from, from + pageSize - 1);

  const { data: rows, count } = await q;
  const list = (rows ?? []) as any[];
  const total = count ?? 0;
  if (list.length === 0) return { rows: [], total };

  // per-page derived columns (best phone, tag count, last call) — page rows only.
  const pageIds = list.map((r) => r.id);
  const contactIds = list.map((r) => r.primary_contact_id).filter(Boolean);
  const sourceIds = list.map((r) => r.source_lead_id).filter(Boolean);
  const [tagRes, phoneRes, callRes] = await Promise.all([
    sb.from("taggables").select("taggable_id").eq("taggable_type", "raw_lead").in("taggable_id", pageIds),
    contactIds.length
      ? sb.from("contact_phones").select("contact_id,number_e164,is_primary,activity_score,status,created_at,dnc").in("contact_id", contactIds)
      : Promise.resolve({ data: [] as any[] }),
    sourceIds.length
      ? sb.from("activities").select("lead_id,disposition,occurred_at").eq("type", "call").in("lead_id", sourceIds).order("occurred_at", { ascending: false })
      : Promise.resolve({ data: [] as any[] }),
  ]);
  const tagCt: Record<string, number> = {};
  for (const t of (tagRes.data ?? []) as any[]) tagCt[t.taggable_id] = (tagCt[t.taggable_id] ?? 0) + 1;
  const phonesByContact: Record<string, any[]> = {};
  for (const p of (phoneRes.data ?? []) as any[]) (phonesByContact[p.contact_id] ??= []).push(p);
  const lastCall: Record<string, string> = {};
  for (const a of (callRes.data ?? []) as any[]) if (!lastCall[a.lead_id]) lastCall[a.lead_id] = a.disposition ?? "—";

  // best phone per contact: score desc -> primary -> untried -> id (mirrors the queue).
  const bestPhone = (cid: string | null) => {
    const arr = cid ? phonesByContact[cid] ?? [] : [];
    const workable = arr.filter((p) => (p.status == null || p.status === "correct") && !p.dnc);
    workable.sort((a, b) =>
      (b.activity_score ?? -1) - (a.activity_score ?? -1) ||
      Number(b.is_primary) - Number(a.is_primary) ||
      (a.status == null ? 0 : 1) - (b.status == null ? 0 : 1));
    return workable[0] ?? arr[0] ?? null;
  };

  const mapped: RawLeadRow[] = list.map((r) => {
    const p = asArray<any>(r.properties)[0] ?? {};
    const flags = p.ownership_flags && typeof p.ownership_flags === "object"
      ? Object.keys(p.ownership_flags).filter((k) => p.ownership_flags[k]).join(", ") : "";
    const bp = bestPhone(r.primary_contact_id);
    return {
      id: r.id,
      record_status: r.record_status,
      campaign_id: r.campaign_id,
      campaign_name: asArray<any>(r.campaigns)[0]?.name ?? null,
      promoted_lead_id: r.promoted_lead_id,
      source_lead_id: r.source_lead_id,
      primary_contact_id: r.primary_contact_id,
      vintage_date: r.vintage_date,
      case_type: r.case_type ?? null,
      call_attempts: r.call_attempts ?? 0,
      do_not_call: !!r.do_not_call,
      property_address: [p.address_line1, p.city, p.county].filter(Boolean).join(", ") || "—",
      owner: p.owner_1_name || "—",
      county: p.county ?? null,
      ownership: flags,
      record_type: Number(p.record_completeness ?? 0) >= 1 ? "Complete" : "Partial",
      list_count: asArray<any>(r.raw_lead_lists).length,
      tag_count: tagCt[r.id] ?? 0,
      phone_count: r.primary_contact_id ? (phonesByContact[r.primary_contact_id]?.length ?? 0) : 0,
      best_number: bp?.number_e164 ?? null,
      best_score: bp?.activity_score ?? null,
      last_call: lastCall[r.source_lead_id] ?? null,
      in_leads: r.record_status === "promoted" || !!r.promoted_lead_id,
    };
  });
  return { rows: mapped, total };
}

// Quick-filter chip counts (head-only count queries).
export async function loadQuickCounts(): Promise<{ all: number; newWeek: number; unassigned: number; neverCalled: number; dnc: number }> {
  const sb = getSupabaseClient();
  const c = (build: (q: any) => any) => build(sb.from("raw_leads").select("*", { count: "exact", head: true }).is("duplicate_of", null)).then((r: any) => r.count ?? 0);
  const [all, newWeek, unassigned, neverCalled, dnc] = await Promise.all([
    c((q: any) => q),
    c((q: any) => q.gte("vintage_date", daysAgoISO(7))),
    c((q: any) => q.is("campaign_id", null)),
    c((q: any) => q.eq("call_attempts", 0)),
    c((q: any) => q.eq("do_not_call", true)),
  ]);
  return { all, newWeek, unassigned, neverCalled, dnc };
}

// Resolve ALL raw_lead ids matching a filter set (for cross-page "select all matching").
export async function idsForFilters(f: GridFilters): Promise<string[]> {
  const sb = getSupabaseClient();
  const search = f.search ? sanitize(f.search) : "";
  const { ids, sourceLeadIds } = await resolveIdConstraints(sb, f);
  if (ids !== null && ids.length === 0) return [];
  if (sourceLeadIds !== null && sourceLeadIds.length === 0) return [];
  const propSel = search ? "id,properties!inner(address_line1,owner_1_name)" : "id";
  let q = sb.from("raw_leads").select(propSel).is("duplicate_of", null).limit(20000);
  if (f.campaignId === "__unassigned__") q = q.is("campaign_id", null);
  else if (f.campaignId) q = q.eq("campaign_id", f.campaignId);
  if (f.caseType) q = q.eq("case_type", f.caseType);
  if (f.doNotCall === "exclude") q = q.eq("do_not_call", false);
  if (f.doNotCall === "only") q = q.eq("do_not_call", true);
  if (f.neverCalled) q = q.eq("call_attempts", 0);
  if (f.newThisWeek) q = q.gte("vintage_date", daysAgoISO(7));
  if (f.vintageFrom) q = q.gte("vintage_date", f.vintageFrom);
  if (f.vintageTo) q = q.lte("vintage_date", f.vintageTo);
  if (ids !== null) q = q.in("id", ids);
  if (sourceLeadIds !== null) q = q.in("source_lead_id", sourceLeadIds);
  if (search) q = q.or(`address_line1.ilike.%${search}%,owner_1_name.ilike.%${search}%`, { referencedTable: "properties" });
  const { data } = await q;
  return (data ?? []).map((r: any) => r.id);
}

// ---- Lists ------------------------------------------------------------------
export async function loadLists(): Promise<ListRow[]> {
  const { data } = await getSupabaseClient().from("list_stacking_lists").select("*").order("title");
  return (data ?? []) as ListRow[];
}
export async function loadListOptions(): Promise<{ id: string; title: string }[]> {
  const { data } = await getSupabaseClient().from("lists").select("id,title").order("title");
  return (data ?? []) as any[];
}
export async function createList(title: string, case_type?: string | null) {
  return getSupabaseClient().from("lists").insert({ title, case_type: case_type || null }).select("id").single();
}
export async function deleteList(id: string) {
  return getSupabaseClient().from("lists").delete().eq("id", id);
}

// ---- Import log (manual imports UNION the dmstack feed run-history) ----------
export async function loadImportLog() {
  const sb = getSupabaseClient();
  const [manual, feed] = await Promise.all([
    sb.from("raw_lead_imports").select("*").order("created_at", { ascending: false }).limit(100),
    sb.from("sync_runs").select("*").order("ran_at", { ascending: false }).limit(100),
  ]);
  const rows = [
    ...((manual.data ?? []) as any[]).map((r) => ({
      source: "manual" as const, at: r.created_at, filename: r.filename ?? "(csv)",
      total: r.rows_total, imported: r.rows_imported, dupes: r.dupes_skipped, dnc: r.dnc_scrubbed,
    })),
    ...((feed.data ?? []) as any[]).map((r) => ({
      source: "dmstack" as const, at: r.ran_at, filename: `${r.trigger ?? "sync"} pull`,
      total: r.candidates, imported: r.pushed, dupes: r.skipped_known, dnc: 0,
    })),
  ];
  // sync_runs read is admin-gated (RLS) — a non-admin simply sees the manual rows.
  return rows.sort((a, b) => (a.at < b.at ? 1 : -1));
}

// ---- Tags (raw-lead scope) --------------------------------------------------
export async function loadTags() {
  const sb = getSupabaseClient();
  const [tags, uses] = await Promise.all([
    sb.from("tags").select("*").order("name"),
    sb.from("taggables").select("tag_id").eq("taggable_type", "raw_lead"),
  ]);
  const ct: Record<string, number> = {};
  for (const u of (uses.data ?? []) as any[]) ct[u.tag_id] = (ct[u.tag_id] ?? 0) + 1;
  return ((tags.data ?? []) as any[]).map((t) => ({ ...t, raw_lead_uses: ct[t.id] ?? 0 }));
}
export async function createTag(name: string, category: string | null, color: string | null) {
  return getSupabaseClient().from("tags").insert({ name, category, color, scope: "any" }).select("id").single();
}
export async function updateTag(id: string, patch: { name?: string; category?: string | null; color?: string | null }) {
  return getSupabaseClient().from("tags").update(patch).eq("id", id);
}
export async function deleteTag(id: string) {
  // taggables FK is on delete cascade — removing a tag untags every lead that had it.
  return getSupabaseClient().from("tags").delete().eq("id", id);
}
export async function updateList(id: string, patch: { title?: string; case_type?: string | null }) {
  return getSupabaseClient().from("lists").update(patch).eq("id", id);
}
export async function canManageTagsLists(): Promise<boolean> {
  const { data } = await getSupabaseClient().rpc("capability_level", { p_cap: "manage_templates_tags" });
  return data === "full";
}
// Map a Supabase/Postgres error to a human, actionable message — the actual fix for
// the "silent / not active" create defect (surface WHY, never a dead no-op).
export function humanError(error: any): string {
  const msg: string = error?.message ?? "";
  const code: string = error?.code ?? "";
  if (code === "42501" || /row-level security|permission denied|violates row-level/i.test(msg))
    return "You need Tags/Lists management permission (Admin or Lead Manager).";
  if (code === "23505" || /duplicate key|already exists/i.test(msg))
    return "That name already exists — pick a different one.";
  return msg || "Something went wrong.";
}

// ---- Bulk actions + dedupe --------------------------------------------------
export async function addToList(rawLeadIds: string[], listId: string) {
  const rows = rawLeadIds.map((raw_lead_id) => ({ raw_lead_id, list_id: listId }));
  return getSupabaseClient().from("raw_lead_lists").upsert(rows, { onConflict: "raw_lead_id,list_id", ignoreDuplicates: true });
}
export async function addTag(rawLeadIds: string[], tagId: string) {
  const rows = rawLeadIds.map((taggable_id) => ({ tag_id: tagId, taggable_type: "raw_lead", taggable_id }));
  return getSupabaseClient().from("taggables").upsert(rows, { onConflict: "tag_id,taggable_type,taggable_id", ignoreDuplicates: true });
}
// S3: campaign options + bulk assign (reuses the assign_raw_leads_to_campaign RPC).
export async function loadCampaignOptions(): Promise<{ id: string; name: string }[]> {
  const { data } = await getSupabaseClient().from("campaigns").select("id,name").eq("audience", "raw_lead").eq("status", "active").order("name");
  return (data ?? []) as any[];
}
export async function assignToCampaign(rawLeadIds: string[], campaignId: string) {
  return getSupabaseClient().rpc("assign_raw_leads_to_campaign", { p_campaign_id: campaignId, p_raw_lead_ids: rawLeadIds });
}
// D64 grid actions -----------------------------------------------------------
export async function removeTag(rawLeadIds: string[], tagId: string) {
  return getSupabaseClient().from("taggables").delete().eq("tag_id", tagId).eq("taggable_type", "raw_lead").in("taggable_id", rawLeadIds);
}
export async function removeFromList(rawLeadIds: string[], listId: string) {
  return getSupabaseClient().from("raw_lead_lists").delete().eq("list_id", listId).in("raw_lead_id", rawLeadIds);
}
export async function markDnc(rawLeadIds: string[]) {
  return getSupabaseClient().from("raw_leads").update({ do_not_call: true }).in("id", rawLeadIds);
}
// "Send to dialer" = assign to a chosen campaign (if not already), then route the
// dialer scoped to it. Dialing is campaign-scoped (D54), so this is assign + route.
export async function sendToDialer(rawLeadIds: string[], campaignId: string) {
  return assignToCampaign(rawLeadIds, campaignId);
}
// client-side CSV of the selected/visible rows (marketing columns).
export function exportSelectedCsv(rows: RawLeadRow[]) {
  const cols = ["property_address", "owner", "case_type", "campaign_name", "record_status", "call_attempts", "last_call", "best_number", "best_score", "vintage_date", "do_not_call"];
  const esc = (v: any) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc((r as any)[c])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `raw-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}
export async function deleteRawLead(id: string) {
  return getSupabaseClient().from("raw_leads").delete().eq("id", id);
}
export async function dedupeByMailing(listId: string | null) {
  return getSupabaseClient().rpc("dedupe_raw_leads_by_mailing", { p_list_id: listId });
}
// S6: "Push to Acquisitions" — graduate the shadow lead into an S&A Lead (gated on
// push_to_acquisitions; idempotent). One call per raw lead (kept simple + non-fatal).
export async function promoteToAcquisitions(rawLeadId: string) {
  return getSupabaseClient().rpc("promote_raw_lead_to_acquisitions", { p_raw_lead_id: rawLeadId });
}
export async function canPushToAcquisitions(): Promise<boolean> {
  const { data } = await getSupabaseClient().rpc("capability_level", { p_cap: "push_to_acquisitions" });
  return data === "full";
}
// D62: reverse a promote — "Return to Marketing" (same push_to_acquisitions cap).
export async function demoteFromAcquisitions(rawLeadId: string) {
  return getSupabaseClient().rpc("demote_from_acquisitions", { p_raw_lead_id: rawLeadId });
}

// ---- Raw-lead marketing detail (S3 / D64) — one rich shape for the full page --
export type RawLeadDetail = {
  raw: any;
  property: any;
  contact: any;
  phones: any[];
  emails: string[];
  campaign: { id: string; name: string } | null;
  lists: { id: string; title: string }[];
  tags: { id: string; name: string; color: string | null }[];
  comms: any[];
};
export async function loadRawLeadDetail(id: string): Promise<RawLeadDetail | null> {
  const sb = getSupabaseClient();
  const { data: rl } = await sb
    .from("raw_leads")
    .select(
      "id,record_status,vintage_date,case_type,source_channel,county,call_attempts,sms_attempts,mail_step," +
        "do_not_call,do_not_mail,next_action_at,reactivation_at,last_mailed_at,campaign_id,promoted_lead_id,source_lead_id,primary_contact_id," +
        "properties(address_line1,city,state,zip,county,owner_1_name,owner_2_name,mailing_address,apn_folio,property_type,source,record_completeness,ownership_flags)," +
        "contacts(first_name,last_name,email)," +
        "campaigns(id,name)",
    )
    .eq("id", id)
    .maybeSingle();
  if (!rl) return null;
  const r = rl as any;
  const [phones, memberships, tags, comms] = await Promise.all([
    r.primary_contact_id
      ? sb.from("contact_phones").select("id,number_e164,phone_type,is_primary,activity_score,status,dnc,created_at").eq("contact_id", r.primary_contact_id).order("is_primary", { ascending: false }).order("activity_score", { ascending: false, nullsFirst: false })
      : Promise.resolve({ data: [] as any[] }),
    sb.from("raw_lead_lists").select("list_id, lists(title)").eq("raw_lead_id", id),
    sb.from("taggables").select("tag_id, tags(name,color)").eq("taggable_type", "raw_lead").eq("taggable_id", id),
    r.source_lead_id
      ? sb.from("activities").select("id,type,direction,body,disposition,duration_sec,recording_url,occurred_at").eq("lead_id", r.source_lead_id).order("occurred_at", { ascending: false }).limit(30)
      : Promise.resolve({ data: [] as any[] }),
  ]);
  const contact = asArray<any>(r.contacts)[0] ?? {};
  return {
    raw: r,
    property: asArray<any>(r.properties)[0] ?? {},
    contact,
    phones: (phones.data ?? []) as any[],
    emails: contact.email ? [contact.email] : [],
    campaign: asArray<any>(r.campaigns)[0] ?? null,
    lists: ((memberships.data ?? []) as any[]).map((m) => ({ id: m.list_id, title: asArray<any>(m.lists)[0]?.title })).filter((x) => x.title),
    tags: ((tags.data ?? []) as any[]).map((t) => ({ id: t.tag_id, ...(asArray<any>(t.tags)[0] ?? {}) })).filter((x) => x.name),
    comms: (comms.data ?? []) as any[],
  };
}
// Full comms thread for the shadow lead (Communication/Activity tabs).
export async function loadRawLeadComms(sourceLeadId: string): Promise<any[]> {
  if (!sourceLeadId) return [];
  const { data } = await getSupabaseClient().from("activities")
    .select("id,type,direction,body,disposition,duration_sec,recording_url,occurred_at,source")
    .eq("lead_id", sourceLeadId).order("occurred_at", { ascending: false }).limit(200);
  return (data ?? []) as any[];
}
// "Log a note" — a note activity on the shadow lead (shared with the S&A lead).
export async function addNote(sourceLeadId: string, body: string) {
  return getSupabaseClient().from("activities").insert({ type: "note", direction: "none", lead_id: sourceLeadId, body, source: "marketing" });
}
// wired do-not-call / do-not-mail toggles on the Marketing tab.
export async function setRawLeadFlags(id: string, patch: { do_not_call?: boolean; do_not_mail?: boolean }) {
  return getSupabaseClient().from("raw_leads").update(patch).eq("id", id);
}
